import React, {
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import pluralize from 'pluralize';
import DOMPurify from 'dompurify';
import {
  ActionPlanCollaboratorsSchema,
  ActionPlanSchema,
  ActionPlanTaskSchema,
  ActionPlanTasksSchema,
  ContactSchema,
  ConversationThreadSchema,
  DealSchema,
} from '../../definitions/schemas';
import { ActionPlanContext } from './ActionPlanContext';
import {
  ActionPlanTaskStatus,
  ActionPlanVisibility,
} from '../../enums/ActionPlans';
import { buildMentionsPayload } from '../mentions/helper';
import { capitalize } from '../../lib/String';
import { ConversableType } from '../../enums/Conversations';
import { ConversationSubmitInput } from '../Conversation/Input';
import { CreateThreadInput } from '../Conversation/Thread';
import { DEFAULT_LANGUAGE } from '../../lib/Language';
import { descendingSort } from '../../lib/Array';
import { Events, Properties } from '../../enums/Analytics';
import {
  flattenTasks,
  getTaskBaseProperties,
  deduplicateCollaborators,
  orderActionPlanTasks,
} from './helper';
import { getProcessedName } from '../../lib/Room';
import { QueryErrorResponse } from '../../graphql/Interface';
import { ShareRoomProps } from './Tasks/Owners/types';
import { useShowConfirm } from '../../lib/hook/useShowConfirm';
import { Values, translationByLang, useI18n } from '../../lib/hook/useI18n';
import ActionPlanController, {
  ActionPlanControllerApi,
} from './ActionPlanController';
import ActionPlansInterface from '../../graphql/ActionPlans/ActionPlansInterface';
import ActionPlansTasksInterface from '../../graphql/ActionPlans/ActionPlanTasksInterface';
import ActionPlanTemplatesInterface from '../../graphql/ActionPlans/ActionPlanTemplatesInterface';
import Analytics from '../../tools/Analytics';
import ConversationThreadsInterface, {
  CreateConversationThreadInput,
} from '../../graphql/ConversationThreads/ConversationThreadsInterface';
import SnackbarStore from '../../stores/observables/SnackbarStore';
import TaskActionsMenu from './Tasks/TaskActionsMenu';
import useResource from '../../lib/hook/useResource';
import UserStore from '../../stores/observables/UserStore';
import { TaskConfigurationProps } from './Tasks/TaskConfigurationProps';
import RoomParticipantsInterface from '../../graphql/Rooms/RoomParticipantsInterface';
import { buildCollaboratorsFromGuests } from './Tasks/Owners/helper';
import { renderLazyComponent } from '../../utils/helpers';
import { TaskDetailsTab } from './Steps/TaskDetailsEnum';

const LazySelectTemplate = renderLazyComponent(
  lazy(() => import('./Steps/SelectTemplate'))
);

const LazyActionPlan = renderLazyComponent(
  lazy(() => import('./Steps/ActionPlan'))
);

const LazyEditTask = renderLazyComponent(
  lazy(() => import('./Steps/EditTask'))
);

const LazyCreateActionPlan = renderLazyComponent(
  lazy(() => import('./Steps/CreateActionPlan'))
);

const LazyTaskDetails = renderLazyComponent(
  lazy(() => import('./Steps/TaskDetails'))
);

const TASK_DELETED_ERROR_MSG = 'task does not exist';

export enum ActionPlanWizardStep {
  CreateActionPlan = 'create-action-plan',
  EditTask = 'edit-task',
  SelectTemplate = 'select-template',
  ViewTaskDetails = 'view-task-details',
  ViewTasks = 'view-tasks',
}

export interface ActionPlanWizardState {
  activeTask?: ActionPlanTaskSchema;
  activeTaskParent?: ActionPlanTaskSchema;
  contentRef: React.RefObject<HTMLDivElement>;
  hasTemplates: boolean;
  loadingTask: boolean;
  name?: string;
  onBack: () => void;
  onClose: () => void;
  onCreateConversationThread: (input: ConversationSubmitInput) => Promise<void>;
  onDeleteActionPlan: () => Promise<void>;
  onUpdateActionPlan: (name: string) => Promise<void>;
  ready: boolean;
  renderTaskMenu: () => React.ReactNode;
  resetScroll: () => void;
  setStep: React.Dispatch<React.SetStateAction<ActionPlanWizardStep>>;
  setTemplatesSearch: React.Dispatch<React.SetStateAction<string>>;
  step: ActionPlanWizardStep;
  taskDetailsTab: TaskDetailsTab;
  templatesSearch: string;
}

export interface ActionPlanWizardProps {
  /**
   * Id of an action plan within the room. If this prop is given, then
   * the editor will be opened. Used for "deep-linking" to a room's
   * action plan, for example, from the rooms listing.
   */
  actionPlanId?: string;
  actionPlanLabel?: string;
  /**
   * Id of a task within the action plan. If a task matches the given id,
   * then the task will be opened when the editor is active. This is used
   * for "deep-linking" to a specific task based on a query string param.
   */
  actionPlanTaskId?: string;
  actionPlanTaskMessageId?: string;
  active: boolean;
  allowDiscussion?: boolean;
  /**
   * Whether guests invitations are allowed when selecting task owners.
   * This is used when the user tries to search for potential owners but
   * they don't exist in the scope of room participants.
   */
  allowInvitations: boolean;
  children: (
    component: React.ReactNode,
    state: ActionPlanWizardState
  ) => React.ReactNode;
  collaborators: ActionPlanCollaboratorsSchema;
  companyName?: string;
  contact?: ContactSchema;
  conversationsRef?: React.MutableRefObject<ConversationThreadSchema[]>;
  deal?: DealSchema;
  displayFeedbackImmediately?: boolean;
  hideOwnerAndDue?: boolean;
  loadFromReview?: boolean;
  onClose: (actionPlan?: ActionPlanSchema) => void;
  organizationName?: string;
  referrer: string;
  resetDeepLink?: () => void;
  roomId?: string;
  ShareRoomDialogProps?: ShareRoomProps;
  userContactId?: string;
  viewOnly?: boolean;
  visibility?: ActionPlanVisibility;
}

const ActionPlanWizard: React.FC<ActionPlanWizardProps> = (
  props: ActionPlanWizardProps
): JSX.Element => {
  const { dirty, setDirty } = useContext(ActionPlanContext);

  const { t } = useI18n();
  const showConfirm = useShowConfirm();

  const {
    actionPlanId,
    actionPlanLabel,
    actionPlanTaskId,
    actionPlanTaskMessageId,
    active,
    allowDiscussion = false,
    allowInvitations,
    children,
    collaborators: initialCollaborators,
    companyName,
    contact,
    conversationsRef,
    deal,
    displayFeedbackImmediately = false,
    hideOwnerAndDue = false,
    loadFromReview,
    onClose,
    organizationName,
    referrer,
    resetDeepLink,
    roomId,
    ShareRoomDialogProps,
    userContactId,
    viewOnly,
    visibility: initialVisibility,
  } = props;

  const isContact = userContactId !== undefined;

  const shouldLoadActionPlan = active && actionPlanId !== undefined;
  const shouldLoadTemplates =
    active && actionPlanId === undefined && !isContact;

  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [tasks, setTasks] = useState<ActionPlanTasksSchema>();
  const [updatedAt, setUpdatedAt] = useState(0);
  const [visibility, setVisibility] = useState<ActionPlanVisibility>();

  /**
   * In-memory reference to collaborators, combining an existing MAP's collaborators
   * and user-selected task owners, to render the owners' info while in the
   * non-open state. This is necessary as there is no other in-memory reference
   * of collaborators for non-saved action plans
   */
  const [collaborators, setCollaborators] =
    useState<ActionPlanCollaboratorsSchema>([]);

  // wizard state
  const [step, setStep] = useState(ActionPlanWizardStep.CreateActionPlan);
  const [loadedActionPlanId, setLoadedActionPlanId] = useState('');
  const [loadingTask, setLoadingTask] = useState(false);

  const [actionPlanCreated, setActionPlanCreated] = useState(false);

  const [activeTask, setActiveTask] = useState<ActionPlanTaskSchema>();
  const [activeTaskParent, setActiveTaskParent] =
    useState<ActionPlanTaskSchema>();
  const [activeTaskLastUpdate, setActiveTaskLastUpdate] = useState(0);
  const [activeTaskConversations, setActiveTaskConversations] = useState<
    ConversationThreadSchema[]
  >([]);

  const [taskDetailsTab, setTaskDetailsTab] = useState(
    TaskDetailsTab.Discussion
  );

  const [templatesSearch, setTemplatesSearch] = useState('');

  const [actionPlanTemplates, actionPlanTemplatesReady] = useResource(
    () => loadActionPlanTemplates(),
    [],
    [],
    shouldLoadTemplates
  );

  const taskOwnersOptions = useMemo(
    () => deduplicateCollaborators(initialCollaborators, collaborators),
    [collaborators, initialCollaborators]
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const shouldAutoFocusDescription = useRef(false);

  // options loaded with an empty search criteria value
  const defaultOptionsLoaded = useRef(false);
  const defaultOptions = useRef<ActionPlanCollaboratorsSchema>([]);

  const actionPlanLoaded = loadedActionPlanId === actionPlanId;
  const areTemplatesReady = !shouldLoadTemplates || actionPlanTemplatesReady;
  const isActionPlanReady = !shouldLoadActionPlan || actionPlanLoaded;
  const isReady = isActionPlanReady && areTemplatesReady;

  const allowOwnerInvitations = allowInvitations && !viewOnly && !!roomId;

  let label = 'mutual action plan';
  if (actionPlanLabel) {
    label = pluralize(actionPlanLabel, 1);
  }

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        if (actionPlanLoaded || !(actionPlanId && shouldLoadActionPlan)) {
          return;
        }

        const actionPlan = await new ActionPlansInterface(userContactId).get({
          id: actionPlanId,
        });

        if (!active) {
          return;
        }

        setStep(ActionPlanWizardStep.ViewTasks);
        const orderedTasks = setActionPlanFields(actionPlan);

        if (actionPlanTaskId) {
          handleDeepLink(actionPlanTaskId, orderedTasks);
        }
      } catch (e) {
        SnackbarStore.show(`${e}`);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionPlanId, actionPlanTaskId, shouldLoadActionPlan]);

  useEffect(() => {
    const singleTaskSteps = [
      ActionPlanWizardStep.EditTask,
      ActionPlanWizardStep.ViewTaskDetails,
    ];

    if (
      singleTaskSteps.includes(step) &&
      taskDetailsTab === TaskDetailsTab.Discussion
    ) {
      resetContentScroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const loadTask = async (id: string) => {
    try {
      setLoadingTask(true);

      const task = await new ActionPlansTasksInterface(userContactId).get({
        id,
      });

      setLoadingTask(false);

      return task;
    } catch (e) {
      const queryError = e as QueryErrorResponse;
      handleTaskFetchError(id, queryError);

      resetActiveTask(ActionPlanWizardStep.ViewTasks);
      setLoadingTask(false);
    }
  };

  const loadActionPlanTemplates = async () => {
    const templates = await new ActionPlanTemplatesInterface().search();

    // since templates are not loaded if an existing action plan is loaded,
    // we don't need to worry about handling that scenario here
    setStep(
      templates.length > 0
        ? ActionPlanWizardStep.SelectTemplate
        : ActionPlanWizardStep.CreateActionPlan
    );

    return templates;
  };

  const handleTaskFetchError = (id: string, queryError: QueryErrorResponse) => {
    if (Array.isArray(queryError)) {
      const msg = queryError[0]?.message;
      /**
       * make sure this error matches the one from the backend
       * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/private/queries/action_plans/get_action_plan_task.rb#L10
       * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/contact/queries/action_plans/get_action_plan_task.rb#L18
       */
      if (id && msg.includes(TASK_DELETED_ERROR_MSG)) {
        onTaskDeleted(id);
        SnackbarStore.show('The task was just deleted');
      }
    } else {
      SnackbarStore.show(`${queryError}`);
    }
  };

  const resetContentScroll = () =>
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    }, 500);

  const resetActiveTask = (step?: ActionPlanWizardStep) => {
    setActiveTask(undefined);
    setActiveTaskParent(undefined);
    setActiveTaskConversations([]);
    setActiveTaskLastUpdate(0);
    setTaskDetailsTab(TaskDetailsTab.Discussion);

    if (resetDeepLink) {
      resetDeepLink();
    }
    if (step) {
      setStep(step);
    }
  };

  const setActionPlanFields = (
    actionPlan: ActionPlanSchema
  ): ActionPlanTasksSchema => {
    const orderedTasks = orderActionPlanTasks(actionPlan.tasks || []);
    setId(actionPlan.id);
    setName(actionPlan.name);
    setTasks(orderedTasks);
    setUpdatedAt(actionPlan.updatedAt);
    setVisibility(initialVisibility || actionPlan.visibility);
    setCollaborators(actionPlan.collaborators || []);

    setLoadedActionPlanId(actionPlan.id);

    return orderedTasks;
  };

  const getActionPlan = (): ActionPlanSchema | undefined => {
    if (!(id && visibility)) {
      return undefined;
    }

    return {
      id,
      name,
      tasks: flattenTasks(tasks || []),
      updatedAt,
      visibility,
    } as ActionPlanSchema;
  };

  const handleClose = (
    actionPlan?: ActionPlanSchema,
    skipConfirmation = false
  ) => {
    if (!dirty || skipConfirmation) {
      onClose(actionPlan);
      setDirty(false);
      return;
    }

    const translation = (key: string, values?: Values) =>
      loadFromReview
        ? translationByLang(key, DEFAULT_LANGUAGE.value, values)
        : t(key, values);

    // @note not the same as useUnsavedChangesConfirm
    showConfirm({
      cancellationText: translation('map.exit.modal.body.cancel'),
      confirmationText: translation('map.exit.modal.body.exit'),
      disablePortal: true,
      title: translation('map.exit.modal.title', { name }),
      description: (
        <>
          <Box component="span" display="block">
            {translation('map.exit.modal.body.title')}
          </Box>
          <Box component="span" display="block">
            {translation('map.exit.modal.body.subtitle')}
          </Box>
        </>
      ),
    }).then(
      () => {
        onClose(actionPlan);
        setDirty(false);
      },
      () => {}
    );
  };

  const handleDeepLink = (taskId: string, tasks: ActionPlanTasksSchema) => {
    // #1 try to find the deep linked task as a parent task
    let task = tasks.find(({ id }) => id === taskId);

    if (!task) {
      // #2 try to find it as a subtask, this time, with the flattened array
      // as the nested one doesn't contain the subtasks
      task = flattenTasks(tasks).find(({ id }) => id === taskId);
    }

    if (task) {
      const tab = actionPlanTaskMessageId
        ? TaskDetailsTab.Discussion
        : undefined;

      onViewTask(task, tasks, tab, true);
    }
  };

  const handleDelete = async () => {
    try {
      if (isContact) {
        return;
      }

      await new ActionPlansInterface().delete({ id });

      handleClose();

      if (displayFeedbackImmediately) {
        SnackbarStore.show(`${capitalize(label)} deleted successfully!`);
      }
    } catch (e) {
      SnackbarStore.show(`${e}`);
    }
  };

  const handleDeleteTask = (api: ActionPlanControllerApi) =>
    api.onDeleteTask(activeTask?.id, activeTask?.name).then((deleted) => {
      if (deleted) {
        resetActiveTask(ActionPlanWizardStep.ViewTasks);
      }
    });

  const onBack = () => {
    if (step === ActionPlanWizardStep.EditTask) {
      setStep(ActionPlanWizardStep.ViewTaskDetails);
    } else if (step === ActionPlanWizardStep.ViewTaskDetails) {
      if (activeTaskParent) {
        onViewTask(activeTaskParent);
      } else {
        resetActiveTask(ActionPlanWizardStep.ViewTasks);
      }
    }

    if (resetDeepLink) {
      resetDeepLink();
    }
  };

  const onActionPlanCreated = (
    actionPlan: ActionPlanSchema,
    fromTemplate: boolean
  ) => {
    setActionPlanFields(actionPlan);
    setActionPlanCreated(true);
    setStep(ActionPlanWizardStep.ViewTasks);

    Analytics.track(Events.MAP_CREATED, {
      [Properties.CREATED_FROM]: fromTemplate ? 'map template' : 'scratch',
      [Properties.ENTERED_FROM]: referrer,
      [Properties.MILESTONE_NUMBER]:
        actionPlan.tasks?.filter((task) => task.milestone).length || 0,
      [Properties.NAME]: actionPlan.name,
      [Properties.TASK_NUMBER]: actionPlan.tasks?.length || 0,
    });
  };

  const onUpdateActionPlan = async (name: string): Promise<void> => {
    try {
      if (isContact) {
        return;
      }

      const response = await new ActionPlansInterface().update({
        id,
        name: getProcessedName(name, deal, contact),
        visibility: visibility!,
      });

      const { actionPlan } = response.updateActionPlan;
      setActionPlanFields(actionPlan);
    } catch (e) {
      SnackbarStore.show(`${e}`);
    }
  };

  const onDeleteActionPlan = () =>
    showConfirm({
      confirmationText: 'Delete',
      disablePortal: true,
      title: `Delete ${label}`,
      useErrorColor: true,
      description: (
        <>
          <Box component="span" display="block">
            <Typography color="textSecondary" component="span">
              {`Are you sure you want to delete the ${name} ${label}?`}
            </Typography>
          </Box>
          <Box component="span" display="block" mt={2}>
            <Typography color="error" component="span">
              {`The deletion would remove this ${label} from the room immediately and
          cannot be undone.`}
            </Typography>
          </Box>
        </>
      ),
    }).then(
      () => handleDelete(),
      () => {}
    );

  /**
   * Updating the reference to the current active task (when viewing
   * task details) in order to maintain the latest updates in memory
   * without requiring an additional request to the server
   *
   * @param resetTaskDetailsTab whether to reset the active tab for task details.
   *                            Used when the last subtask of the active task is deleted.
   */
  const updateActiveTask = (
    orderedTasks: ActionPlanTasksSchema,
    resetTaskDetailsTab = false
  ) => {
    if (!activeTask) {
      return;
    }

    if (activeTask.parentTaskId) {
      const parent = orderedTasks.find(
        ({ id }) => id === activeTask.parentTaskId
      );

      setActiveTask(parent?.subtasks?.find(({ id }) => id === activeTask.id));
      setActiveTaskParent(parent);
      setActiveTaskLastUpdate(Date.now());
      return;
    }

    const newActiveTask = orderedTasks.find(({ id }) => id === activeTask.id);
    setActiveTask(newActiveTask);
    setActiveTaskLastUpdate(Date.now());

    if (
      resetTaskDetailsTab &&
      taskDetailsTab === TaskDetailsTab.Subtasks &&
      !newActiveTask?.subtasks?.length
    ) {
      setTaskDetailsTab(TaskDetailsTab.Discussion);
    }
  };

  /**
   * Updating the local state to tell the action plan that there's been activity.
   * Even if this is reflected on the database, we don't want to fetch
   * the whole action plan just to have the latest value, so we do this
   * trick instead.
   */
  const onUpdateLastActivity = (newValue: number) => {
    setUpdatedAt(newValue);
  };

  const onTaskSaved = (
    task: ActionPlanTaskSchema,
    overwriteSubtasks = false
  ) => {
    onUpdateLastActivity(task.updatedAt);
    setTasks((prevTasks = []) => {
      let tasks = prevTasks;
      if (overwriteSubtasks) {
        // reassigning the task in the array if its subtasks require an overwrite
        // before flattening the array, so it keeps the most recent value for the state
        tasks = prevTasks.map((existingTask) =>
          existingTask.id !== task.id ? existingTask : task
        );
      }

      const newTasks: ActionPlanTasksSchema = [];

      let exists = false;

      flattenTasks(tasks).forEach((existingTask) => {
        const isModified = existingTask.id === task.id;
        exists = exists || isModified;
        newTasks.push(isModified ? task : existingTask);
      });

      const orderedTasks = orderActionPlanTasks(
        exists ? newTasks : [...newTasks, task]
      );

      updateActiveTask(orderedTasks);

      return orderedTasks;
    });
  };

  const onTaskDeleted = (id: string) => {
    setTasks((tasks = []) => {
      const newTasks: ActionPlanTasksSchema = [];

      let deletedTask: ActionPlanTaskSchema | undefined;

      flattenTasks(tasks).forEach((task) => {
        if (task.id === id) {
          deletedTask = task;
          return;
        }

        if (task.parentTaskId === id) {
          return;
        }

        // updating the pointer of the deleted task's next task,
        // to maintain the order after that item is deleted
        if (task.previousTaskId === id) {
          const previousTaskId = deletedTask?.previousTaskId;
          newTasks.push({ ...task, previousTaskId });

          return;
        }

        newTasks.push(task);
      });

      const orderedTasks = orderActionPlanTasks(newTasks);
      updateActiveTask(orderedTasks, true);

      return orderedTasks;
    });
  };

  /**
   * Switches to the task details view for the given task. If the task is a
   * subtask, then its parent is set to the state in order for the wizard
   * to return to that parent task when the user exits the subtask
   *
   * @param forcedTasks   array of tasks where to find a subtask's parent, it's
   *                      required only for the deeplink use case, as the tasks from
   *                      from the state aren't available because of React
   * @param forcedTab     task details tab to force the view tasks screen to land on.
   *                      Used when deep linked to a parent task comment.
   * @param useLocalValue whether to use the given param instead of requesting the
   *                      server, used only for the deeplink use case
   */
  const onViewTask = async (
    task: ActionPlanTaskSchema,
    forcedTasks?: ActionPlanTasksSchema,
    forcedTab?: TaskDetailsTab,
    useLocalValue = false
  ) => {
    const tasksArr = forcedTasks || tasks;

    /**
     * Resetting just in case we're moving from a parent to a subtask or
     * viceversa, this helps React flush the state, mainly for the issue that
     * the conversations were being loaded for the parent and the subtask
     * at the same time
     */
    resetActiveTask();
    setStep(ActionPlanWizardStep.ViewTaskDetails);

    const latestVersion = useLocalValue ? task : await loadTask(task.id);
    if (!latestVersion) {
      return;
    }

    const { subtasks } = task;
    let tab = subtasks?.length
      ? TaskDetailsTab.Subtasks
      : TaskDetailsTab.Discussion;

    if (forcedTab) {
      tab = forcedTab;
    }

    let taskParent: ActionPlanTaskSchema | undefined;
    if (latestVersion.parentTaskId) {
      taskParent = tasksArr?.find(
        ({ id }) => id === latestVersion.parentTaskId
      );
    }

    setActiveTaskConversations([]);
    setActiveTaskParent(taskParent);
    setActiveTask({ ...latestVersion, subtasks });
    setTaskDetailsTab(tab);
  };

  const onEditTask = async (
    task: ActionPlanTaskSchema,
    autoFocusDescription = false
  ) => {
    shouldAutoFocusDescription.current = autoFocusDescription;
    setStep(ActionPlanWizardStep.EditTask);
    const latestVersion = await loadTask(task.id);
    setActiveTask(
      !latestVersion ? undefined : { ...latestVersion, subtasks: task.subtasks }
    );
  };

  const onCancelEditTask = () => {
    setStep(ActionPlanWizardStep.ViewTaskDetails);
    setDirty(false);
  };

  const onTaskDetailsTabChange = (tab: TaskDetailsTab) => {
    setTaskDetailsTab(tab);
  };

  const onTaskReordered = async (
    id: string,
    previousTaskId: string | undefined,
    orderedTasks: ActionPlanTasksSchema
  ) => {
    try {
      /**
       * Setting the tasks in their new order, but not yet with their updated pointers,
       * so the changes are reflected in the UI immediately. Otherwise, when
       * the user drops a task, it would come back to its previous position
       * while the API request is being made, once it finishes, the task then
       * jumps to its new position.
       */
      setTasks(orderedTasks);
      updateActiveTask(orderedTasks);

      const result = await new ActionPlansTasksInterface(userContactId).reorder(
        {
          id,
          previousTaskId,
        }
      );

      const affectedTasks = result.reorderActionPlanTask.tasks;
      const affectedTasksDates = affectedTasks
        .map(({ updatedAt }) => updatedAt)
        .sort(descendingSort);

      setUpdatedAt(affectedTasksDates[0]);

      setTasks((tasks = []) => {
        const orderedTasks = orderActionPlanTasks(
          flattenTasks(tasks).map((task) => {
            const affectedTask = affectedTasks.find(({ id }) => id === task.id);
            return affectedTask ?? task;
          })
        );

        updateActiveTask(orderedTasks);
        return orderedTasks;
      });
    } catch (e) {
      SnackbarStore.show(`${e}`);
    }
  };

  const onTaskDetailsSubtasksReordered = (
    sourceTaskId: string,
    previousTaskId: string | undefined,
    orderedSubtasks: ActionPlanTasksSchema
  ) =>
    onTaskReordered(
      sourceTaskId,
      previousTaskId,
      (tasks || []).map((task) =>
        task.id !== activeTask?.id
          ? task
          : { ...task, subtasks: orderedSubtasks }
      )
    );

  const buildConversationPayload = (input: CreateThreadInput) => {
    let payload: CreateConversationThreadInput = {
      ...input,
      conversableId: activeTask?.id!,
      conversableType: ConversableType.ActionPlanTask,
    };

    if (input.message) {
      payload = {
        ...payload,
        message: DOMPurify.sanitize(input.message),
        mentions: buildMentionsPayload(input.message),
      };
    }

    return payload;
  };

  const onCreateConversationThread = async (
    input: ConversationSubmitInput
  ): Promise<void> => {
    try {
      const response = await new ConversationThreadsInterface(
        userContactId
      ).create(buildConversationPayload(input));

      /**
       * Storing the message that was sent into a convenience array that
       * is used by the task details to display this message. This is done
       * this way because we need to combine the messages originally fetched
       * from  the server with the messages sent by the user.
       */
      const { conversationThread } = response.createConversationThread;
      setActiveTaskConversations((prev) => [...prev, conversationThread]);
      setActiveTaskLastUpdate(Date.now());
      onUpdateLastActivity(conversationThread.createdAt);

      /**
       * Increasing the counter of conversation messages for the task
       * in the task list, to avoid having to re-fetch all the tasks
       * just for keeping that counter updated. This is for the scenario
       * where users enter and view task details, send a message and
       * go back to the tasks list.
       */
      setTasks((tasks = []) =>
        orderActionPlanTasks(
          flattenTasks(tasks).map((task) => {
            if (task.id !== activeTask?.id) {
              return task;
            }

            const updatedTask = {
              ...task,
              conversationThreadsCount: task.conversationThreadsCount + 1,
            };

            setActiveTask(updatedTask);
            return updatedTask;
          })
        )
      );

      if (activeTask) {
        trackConversationEvent(activeTask, conversationThread);
      }

      resetContentScroll();
    } catch (e) {
      SnackbarStore.show(`${e}`);
    }
  };

  const onTaskPointerConflict = useCallback(
    async () => {
      try {
        if (!actionPlanId) {
          return;
        }
        const actionPlan = await new ActionPlansInterface(userContactId).get({
          id: actionPlanId,
        });

        setActionPlanFields(actionPlan);
      } catch (e) {
        SnackbarStore.show(`${e}`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actionPlanId, userContactId]
  );

  const searchParticipants = async (
    search: string
  ): Promise<ActionPlanCollaboratorsSchema> => {
    const isSearching = search.trim().length > 0;

    // skip search if the search criteria is empty and the default options are loaded
    // to make it seem faster to users
    if (!isSearching && defaultOptionsLoaded.current) {
      return defaultOptions.current;
    }

    const participants = await new RoomParticipantsInterface(
      userContactId
    ).search({ roomId, search });

    const options = buildCollaboratorsFromGuests(participants);

    // keep a reference to the default options when they're loaded for the first time
    if (!isSearching && !defaultOptionsLoaded.current) {
      defaultOptions.current = options;
      defaultOptionsLoaded.current = true;
    }

    return options;
  };

  const trackConversationEvent = (
    task: ActionPlanTaskSchema,
    conversationThread: ConversationThreadSchema
  ) => {
    const event = UserStore.get()
      ? Events.MAP_TASK_USER_COMMENT_ADDED
      : Events.MAP_TASK_CONTACT_COMMENT_ADDED;

    Analytics.track(event, {
      ...getTaskBaseProperties(task, referrer),
      [Properties.NUMBER_ATTACHMENTS]:
        conversationThread.attachments?.length ?? 0,
      [Properties.NUMBER_MENTIONS]: conversationThread?.mentions?.length || 0,
    });
  };

  const renderTaskMenu =
    (api: ActionPlanControllerApi) => (): React.ReactNode => {
      if (!activeTask) {
        return undefined;
      }

      const isCompleted = activeTask.status === ActionPlanTaskStatus.Completed;

      return (
        <TaskActionsMenu
          completeable={!isCompleted}
          incompleteable={isCompleted}
          onChangeStatus={(status) =>
            api.onUpdateTaskStatus(activeTask, status)
          }
          onDelete={() => handleDeleteTask(api)}
          onEdit={() => onEditTask(activeTask)}
        />
      );
    };

  const renderLoadingState = (): JSX.Element => (
    <Box>
      <Skeleton height={32} width="100%" />
      <Skeleton height={16} width="80%" />
      <Skeleton height={16} width="60%" />
      <Skeleton height={16} width="80%" />
    </Box>
  );

  const renderContent = (api: ActionPlanControllerApi): React.ReactNode => {
    if (!isReady) {
      return renderLoadingState();
    }

    // Step #1-A: select MAP template (if the org has templates)
    if (step === ActionPlanWizardStep.SelectTemplate) {
      return (
        <LazySelectTemplate
          actionPlanTemplates={actionPlanTemplates}
          contact={contact}
          deal={deal}
          onActionPlanCreated={(actionPlan: ActionPlanSchema) =>
            onActionPlanCreated(actionPlan, true)
          }
          search={templatesSearch}
        />
      );
    }

    const ownerSelectionProps: TaskConfigurationProps['OwnerSelectionProps'] = {
      allowInvitations: allowOwnerInvitations,
      // collaborators obtained from the fetched action plan data
      collaborators,
      companyName,
      hideOwnerAndDue,
      // options available only in-memory (room contacts OR invited guests)
      options: taskOwnersOptions,
      organizationName,
      searchParticipants,
      ShareRoomDialogProps,
      setCollaborators,
    };

    // Step #2: action plan tasks (task lists CRUD)
    if (step === ActionPlanWizardStep.ViewTasks && id !== undefined) {
      return (
        <LazyActionPlan
          api={api}
          autofocusTaskCreation={!actionPlanId && actionPlanCreated}
          id={id}
          onViewTask={onViewTask}
          OwnerSelectionProps={ownerSelectionProps}
          taskFetchErrorHandler={handleTaskFetchError}
          tasks={tasks}
          userContactId={userContactId}
          viewOnly={viewOnly}
        />
      );
    }

    // Step #3-A: view action plan task details (task CRUD + conversations)
    if (step === ActionPlanWizardStep.ViewTaskDetails) {
      return (
        <LazyTaskDetails
          actionPlanId={id}
          actionPlanTaskMessageId={actionPlanTaskMessageId}
          activeTab={taskDetailsTab}
          allowDiscussion={allowDiscussion}
          api={api}
          collaborators={collaborators}
          conversations={activeTaskConversations}
          conversationsRef={conversationsRef}
          loading={loadingTask}
          onEdit={() => onEditTask(activeTask!, true)}
          onSubtasksReordered={onTaskDetailsSubtasksReordered}
          onTabChange={onTaskDetailsTabChange}
          onViewSubtask={onViewTask}
          OwnerSelectionProps={ownerSelectionProps}
          resetContentScroll={resetContentScroll}
          task={activeTask}
          taskFetchErrorHandler={handleTaskFetchError}
          taskLastUpdate={activeTaskLastUpdate}
          userContactId={userContactId}
          viewOnly={viewOnly}
        />
      );
    }

    // Step #3-B: edit action plan task
    if (step === ActionPlanWizardStep.EditTask) {
      return (
        <LazyEditTask
          autoFocusDescription={shouldAutoFocusDescription.current}
          loading={loadingTask}
          onCancel={onCancelEditTask}
          onDeleteTask={() => handleDeleteTask(api)}
          onTaskUpdated={onCancelEditTask}
          onUpdateTask={api.onUpdateTask}
          OwnerSelectionProps={ownerSelectionProps}
          task={activeTask}
        />
      );
    }

    // Step #1-B: create action plan (default state)
    if (step === ActionPlanWizardStep.CreateActionPlan) {
      return (
        <LazyCreateActionPlan
          onActionPlanCreated={(actionPlan: ActionPlanSchema) =>
            onActionPlanCreated(actionPlan, false)
          }
          onCancel={handleClose}
        />
      );
    }

    return <></>;
  };

  return (
    <ActionPlanController
      actionPlanId={actionPlanId}
      displayFeedbackImmediately={displayFeedbackImmediately}
      onTaskDeleted={onTaskDeleted}
      onTaskPointerConflict={onTaskPointerConflict}
      onTaskReordered={onTaskReordered}
      onTaskSaved={onTaskSaved}
      referrer={referrer}
      taskOwnersOptions={taskOwnersOptions}
      userContactId={userContactId}
    >
      {(api) =>
        children(renderContent(api), {
          activeTask,
          activeTaskParent,
          contentRef,
          hasTemplates: actionPlanTemplates.length > 0,
          loadingTask,
          name,
          onBack,
          onClose: () => handleClose(getActionPlan()),
          onCreateConversationThread,
          onDeleteActionPlan,
          onUpdateActionPlan,
          ready: isReady,
          renderTaskMenu: renderTaskMenu(api),
          resetScroll: resetContentScroll,
          setStep,
          setTemplatesSearch,
          step,
          taskDetailsTab,
          templatesSearch,
        })
      }
    </ActionPlanController>
  );
};

export default ActionPlanWizard;
